import { useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import api, { analyticsAPI } from '../utils/api';
import { getToken } from '../utils/auth';

// Same API base the axios instance in utils/api.js derives from — on the
// split-origin (Cloudflare Pages + Cloud Run) deploy a relative /api/... path
// resolves against the Pages host, not the backend, so the unload flush must
// target this absolute base.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Unload-time flush transport.
//
// The /api/analytics/* endpoints are authenticated (Depends(get_current_user)),
// so a request without an Authorization header is rejected 401. navigator.sendBeacon
// CANNOT set request headers at all, and it reports success (returns true) merely for
// QUEUEING the payload — so a beacon to these endpoints is happily queued by the browser
// yet 401'd by the server, silently dropping the research telemetry with no false return
// to fall back on. A fetch with { keepalive: true } is the only unload-safe transport that
// can carry the Bearer token, so we use it directly instead of sendBeacon. Errors are
// swallowed so the flush can never block page unload.
const keepaliveFlush = (path, payload) => {
  const token = getToken();
  fetch(`${API_BASE}${path}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  }).catch(() => {});
};

const ACTION_BUFFER_MAX = 50;
const ESCALATION_BUFFER_MAX = 20;
const FLUSH_INTERVAL_MS = 10000; // 10 seconds

// ============================================================================
// B-2: persistent, per-user, idempotent action-log OUTBOX (localStorage).
//
// The old flush was fire-and-forget: it cleared the in-memory buffer BEFORE the
// POST and swallowed the .catch, so a network cut / lost response silently dropped
// research telemetry. This replaces that with a durable outbox:
//   * Each flush turns the buffer into ONE batch with a stable batch_id + a
//     per-session monotonic sequence, and PERSISTS it to localStorage first.
//   * A batch leaves the outbox ONLY after the server confirms receipt (any 2xx —
//     a 200 "already_received" from the idempotent endpoint counts). Unconfirmed
//     batches survive a reload and resend on the next startup.
//   * SHARED-DEVICE PRIVACY (minor school context): the outbox + the sequence
//     counters are NAMESPACED BY USER ID, so a student only ever reads/sends their
//     OWN batches (quarantine). On a user-switch the departed student's outbox is
//     CLEARED. Backed up server-side by the endpoint's session-ownership check
//     (a foreign batch is 422, never attributed to the new user).
// ============================================================================
const OUTBOX_KEY_PREFIX = 'graphTutor::actionOutbox::v1::';
const SEQ_KEY_PREFIX = 'graphTutor::actionSeq::v1::';
const ACTIVE_USER_KEY = 'graphTutor::actionOutbox::v1::__activeUser__';
// Bounded quarantine: cap a single user's outbox so a device that stays offline for a
// long time cannot grow localStorage without limit. Oldest batches are dropped first.
const OUTBOX_MAX_BATCHES = 200;

const outboxKey = (userId) => `${OUTBOX_KEY_PREFIX}${userId}`;
const seqKey = (userId, sessionId) => `${SEQ_KEY_PREFIX}${userId}::${sessionId}`;

const safeGet = (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } };
const safeSet = (key, val) => { try { localStorage.setItem(key, val); } catch (e) { /* quota / disabled */ } };
const safeRemove = (key) => { try { localStorage.removeItem(key); } catch (e) { /* disabled */ } };

const genBatchId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `b-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const readOutbox = (userId) => {
  const raw = safeGet(outboxKey(userId));
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};

const writeOutbox = (userId, batches) => {
  const trimmed = batches.length > OUTBOX_MAX_BATCHES
    ? batches.slice(batches.length - OUTBOX_MAX_BATCHES)
    : batches;
  safeSet(outboxKey(userId), JSON.stringify(trimmed));
};

// Monotonic per-(user, session) batch sequence, PERSISTED so it stays continuous across a
// page reload (a resumed session keeps counting up, never reusing or skipping a number).
const nextSequence = (userId, sessionId) => {
  const cur = parseInt(safeGet(seqKey(userId, sessionId)) || '0', 10) || 0;
  const next = cur + 1;
  safeSet(seqKey(userId, sessionId), String(next));
  return next;
};
const currentSequence = (userId, sessionId) =>
  parseInt(safeGet(seqKey(userId, sessionId)) || '0', 10) || 0;

// Clear a departed user's quarantined outbox + all their per-session sequence counters
// (shared-device user-switch). Their unsent batches are intentionally dropped rather than
// ever risk being resent under, or lingering for, the next student on the device.
const clearUserOutbox = (userId) => {
  safeRemove(outboxKey(userId));
  try {
    const prefix = `${SEQ_KEY_PREFIX}${userId}::`;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) toRemove.push(k);
    }
    toRemove.forEach(safeRemove);
  } catch (e) { /* localStorage disabled */ }
};

export function useActionLogger({ sessionId, userId, stampClientTime = false, problemId = null }) {
  const actionBufferRef = useRef([]);
  // Phase A (instrumentation flag): stamp the device's event time on each record.
  // Records are flushed in 10 s batches and the server's created_at is its RECEIVE
  // time, so without this the order and spacing of events inside a batch is lost.
  // Off by default: when the flag is off the request bodies are byte-identical to
  // before (the server would null the value anyway).
  const stampRef = useRef(stampClientTime);
  useLayoutEffect(() => {
    stampRef.current = stampClientTime;
  });
  // Phase A (M7, PI ruling 2026-10-08 item 19): with the same flag, stamp the problem on
  // screen, so the server can tie the event to its task and the research export can leave
  // out a practice task's events without dropping the rest of the lesson. The COMMITTED
  // problem (layout effect), like the task generation in ProblemIntegrationView; the server
  // resolves it to a task only if it is the student's own problem in the record's lesson.
  const problemRef = useRef(problemId);
  useLayoutEffect(() => {
    problemRef.current = problemId;
  });
  const escalationBufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  const drainingRef = useRef(false);
  // Latest ids for use inside the stable async callbacks + the unload handler. Assigned
  // during render so it is always current before any effect runs (idempotent write).
  const idsRef = useRef({ sessionId, userId });
  idsRef.current = { sessionId, userId };

  // Move the in-memory action buffer into ONE persisted outbox batch. Returns true if a
  // batch was enqueued. With no authenticated user or session to bind/namespace against,
  // fall back to a best-effort direct POST (NOT persisted) rather than write under an
  // ambiguous namespace that another student could later share on a shared device.
  const enqueueBatch = useCallback(() => {
    const records = actionBufferRef.current;
    if (records.length === 0) return false;
    const { sessionId: sid, userId: uid } = idsRef.current;
    actionBufferRef.current = [];
    if (!uid || !sid) {
      analyticsAPI.batchActionLogs(records).catch(() => {});
      return false;
    }
    // Single-session batches (P2). The buffer is a ref that survives a sessionId prop change and no
    // effect flushes it on that change, so records logged under different sessions can straddle ONE
    // buffer (a session boundary crossed inside the 10s flush window). The backend derives a batch's
    // session (ownership check + receipt + heartbeat denominator) from records[0].session_id and 422s
    // the WHOLE batch if ANY record's session is unowned; a mixed-session batch would therefore drop a
    // still-owned session's records as COLLATERAL when the other session is withdrawn/erased, and
    // mis-attribute the missing-rate denominator. Group by each record's OWN session_id so every
    // enqueued batch is single-session — a per-session 422 then only ever drops that one session.
    const bySession = new Map();
    records.forEach((rec) => {
      const rsid = rec.session_id != null ? rec.session_id : sid;
      const group = bySession.get(rsid);
      if (group) { group.push(rec); } else { bySession.set(rsid, [rec]); }
    });
    const outbox = readOutbox(uid);
    bySession.forEach((recs, rsid) => {
      const seq = nextSequence(uid, rsid);
      outbox.push({ batch_id: genBatchId(), sequence: seq, session_id: rsid, records: recs });
    });
    writeOutbox(uid, outbox);
    return true;
  }, []);

  // Send every persisted batch for the CURRENT user, removing each ONLY after the server
  // confirms receipt. Stops at the first failure and retries on the next flush/startup.
  // Reads ONLY the current user's namespace, so a different student's quarantined batches
  // are never sent under this user.
  const drainOutbox = useCallback(async () => {
    if (drainingRef.current) return;
    const { userId: uid } = idsRef.current;
    if (!uid) return;
    drainingRef.current = true;
    try {
      const outbox = readOutbox(uid);
      for (let i = 0; i < outbox.length; i += 1) {
        const batch = outbox[i];
        let ok = false;
        let permanentReject = false;
        try {
          const resp = await api.post('/api/analytics/action-logs', {
            records: batch.records,
            batch_id: batch.batch_id,
            sequence: batch.sequence,
            // Let an arriving batch advance the server-observed denominator.
            highest_generated_seq: currentSequence(uid, batch.session_id),
          });
          ok = resp && resp.status >= 200 && resp.status < 300;
        } catch (e) {
          // A 422 is the server's PERMANENT rejection of THIS batch: its session isn't owned by
          // the caller (e.g. deleted mid-flight, or it belongs to another student on a shared
          // device), its batch_id was already claimed by another user, or it exceeds the size
          // cap. None of those become acceptable on a retry, so a poison 422 at the head would
          // otherwise block every LATER batch this user has until OUTBOX_MAX_BATCHES eviction.
          // Quarantine-drop just this one and keep draining. Every OTHER failure (network / 5xx /
          // 401 token-not-ready) is TRANSIENT -> keep the batch and stop, resending from the head
          // on the next flush/startup (preserving order + the zero-loss guarantee).
          const status = e && e.response && e.response.status;
          permanentReject = status === 422;
          ok = false;
        }
        if (!ok && !permanentReject) break;
        // Confirmed (2xx) OR permanently rejected (422) -> remove this batch from the (possibly
        // newly-appended) outbox. Dropping a 422 loses nothing the server would ever have stored
        // (it was never written), and never duplicates: idempotency is keyed by batch_id, and a
        // dropped batch is simply gone rather than re-sent.
        const remaining = readOutbox(uid).filter((b) => b.batch_id !== batch.batch_id);
        writeOutbox(uid, remaining);
      }
    } finally {
      drainingRef.current = false;
    }
  }, []);

  // Expected-volume signal (session-end manifest / periodic heartbeat): tell the server the
  // highest sequence generated for this session so it can compute a missing-rate against a
  // server-OBSERVED denominator. Best-effort.
  const sendHeartbeat = useCallback(() => {
    const { sessionId: sid, userId: uid } = idsRef.current;
    if (!uid || !sid) return;
    const hgs = currentSequence(uid, sid);
    if (hgs <= 0) return;
    api.post('/api/analytics/action-logs/heartbeat', {
      session_id: sid,
      highest_generated_seq: hgs,
    }).catch(() => {});
  }, []);

  const flushActions = useCallback(() => {
    enqueueBatch();
    drainOutbox();
  }, [enqueueBatch, drainOutbox]);

  const flushEscalations = useCallback(() => {
    // Escalations stay best-effort: B-2's idempotency (batch_id receipt table) is action-log
    // only, and a persistent-without-idempotency escalation outbox would risk DUPLICATE
    // escalation rows (corrupting the escalation count) on a resend. Low-volume, so unchanged.
    const escalations = escalationBufferRef.current;
    if (escalations.length > 0) {
      escalationBufferRef.current = [];
      analyticsAPI.batchEscalationLogs(escalations).catch(() => {});
    }
  }, []);

  const flushAll = useCallback(() => {
    flushActions();
    flushEscalations();
  }, [flushActions, flushEscalations]);

  // Startup / user-switch: on a shared device, clear a DEPARTED student's quarantined outbox,
  // then resend the CURRENT student's unconfirmed batches (zero-loss across reloads) and send a
  // heartbeat so the server knows the resumed session's denominator.
  useEffect(() => {
    if (!userId) return;
    const prev = safeGet(ACTIVE_USER_KEY);
    if (prev && prev !== String(userId)) {
      clearUserOutbox(prev);
    }
    safeSet(ACTIVE_USER_KEY, String(userId));
    drainOutbox();
    sendHeartbeat();
  }, [userId, drainOutbox, sendHeartbeat]);

  // Periodic flush
  useEffect(() => {
    flushTimerRef.current = setInterval(flushAll, FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(flushTimerRef.current);
      flushAll(); // flush remaining on unmount
    };
  }, [flushAll]);

  // Flush on page unload. Persist any buffered actions as a final batch, then keepalive-send
  // the outbox (best-effort — unconfirmed batches stay and resend idempotently on next startup,
  // so a lost response never duplicates) plus a session-end manifest heartbeat. Uses a keepalive
  // fetch (NOT sendBeacon) because the endpoints are authenticated — see keepaliveFlush above.
  useEffect(() => {
    const handleBeforeUnload = () => {
      const { sessionId: sid, userId: uid } = idsRef.current;
      enqueueBatch(); // persist buffered records first (survives the unload)
      if (uid) {
        const outbox = readOutbox(uid);
        outbox.forEach((batch) => {
          keepaliveFlush('/api/analytics/action-logs', {
            records: batch.records,
            batch_id: batch.batch_id,
            sequence: batch.sequence,
            highest_generated_seq: currentSequence(uid, batch.session_id),
          });
        });
        if (sid) {
          const hgs = currentSequence(uid, sid);
          if (hgs > 0) {
            keepaliveFlush('/api/analytics/action-logs/heartbeat', {
              session_id: sid,
              highest_generated_seq: hgs,
            });
          }
        }
      }
      const escalations = escalationBufferRef.current;
      if (escalations.length > 0) {
        keepaliveFlush('/api/analytics/escalation-logs', { records: escalations });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enqueueBatch]);

  const logAction = useCallback((record) => {
    actionBufferRef.current.push({
      session_id: sessionId,
      ...record,
      ...(stampRef.current && record?.client_event_at_ms == null
        ? { client_event_at_ms: Date.now() }
        : {}),
      ...(stampRef.current && problemRef.current && record?.problem_id == null
        ? { problem_id: problemRef.current }
        : {}),
    });
    if (actionBufferRef.current.length >= ACTION_BUFFER_MAX) {
      flushActions();
    }
  }, [sessionId, flushActions]);

  const logEscalation = useCallback((record) => {
    escalationBufferRef.current.push({
      session_id: sessionId,
      ...record,
      ...(stampRef.current && record?.client_event_at_ms == null
        ? { client_event_at_ms: Date.now() }
        : {}),
      ...(stampRef.current && problemRef.current && record?.problem_id == null
        ? { problem_id: problemRef.current }
        : {}),
    });
    if (escalationBufferRef.current.length >= ESCALATION_BUFFER_MAX) {
      flushEscalations();
    }
  }, [sessionId, flushEscalations]);

  return { logAction, logEscalation, flushAll };
}
